import React from "react";
import { Link } from "react-router-dom";
import useSEO from "../hooks/useSEO";

const CLT_POINTS = [
  {
    t: "Bois massif",
    d: "Panneaux CLT en épicéa de 60 mm, structurels et porteurs.",
  },
  {
    t: "Enveloppe isolée",
    d: "Structure CLT associée à une isolation en fibre de bois pour améliorer le confort thermique.",
  },
  {
    t: "Finition intérieure naturelle",
    d: "Le bois reste apparent à l'intérieur et crée une atmosphère chaleureuse, sobre et qualitative.",
  },
  {
    t: "Confort d'usage",
    d: "Une structure stable, une bonne inertie et une ambiance intérieure adaptée à un usage touristique, saisonnier ou résidentiel.",
  },
  {
    t: "Fabrication rationnelle",
    d: "Une conception pensée pour l'atelier, la répétabilité et la maîtrise des délais.",
  },
  {
    t: "Adapté à la gamme",
    d: "Une logique constructive commune aux six habitats de la gamme — Micro, Studio, Nido, Lodge T3, Lodge T4 et Twin.",
  },
  {
    t: "Identité architecturale",
    d: "Une matérialité bois visible, qui distingue les habitats YONO des solutions standardisées du marché.",
  },
  {
    t: "Durabilité",
    d: "Une construction robuste, pensée pour un usage régulier et une exploitation dans le temps.",
  },
];

const STEPS = [
  {
    t: "Fabrication en atelier",
    d: "Préassemblage complet en environnement contrôlé pour une qualité constante.",
  },
  {
    t: "Livré prêt à raccorder",
    d: "Modules transportés par camion, livrés équipés et prêts à être raccordés.",
  },
  {
    t: "Manutention facilitée",
    d: "Structure prévue pour la manipulation par chariot élévateur, avec passages de fourche renforcés.",
  },
  {
    t: "Pose sur pieds métalliques",
    d: "Installation sans fondation lourde, adaptable aux contraintes du terrain.",
  },
];

const ENGAGEMENTS = [
  {
    t: "Bois issu de forêts gérées durablement",
    d: "Nous privilégions des bois issus de forêts gérées durablement, sélectionnés pour leur qualité, leur stabilité et leur performance dans le temps.",
  },
  {
    t: "Fabrication française",
    d: "Les habitats sont conçus, fabriqués et assemblés en France, avec un réseau de partenaires locaux.",
  },
  {
    t: "Durabilité",
    d: "Une conception attentive à l'impact environnemental, à la durée de vie et à la réparabilité des habitats.",
  },
  {
    t: "Répétabilité industrielle",
    d: "Des formats standardisés, une logique constructive claire et une qualité homogène d'un projet à l'autre.",
  },
  {
    t: "Robustesse",
    d: "Structure CLT, isolation fibre de bois, bardage épicéa teinté et choix techniques pensés pour un usage régulier.",
  },
  {
    t: "Accompagnement projet",
    d: "Un suivi adapté à votre site, à votre calendrier de livraison et à vos contraintes d'exploitation.",
  },
];

export default function Fabrication() {
  useSEO({
    title: "Fabrication — Structure bois massif CLT",
    description:
      "Toute la gamme YONO est construite à partir de panneaux CLT en épicéa de 60 mm. Préfabrication française en atelier, qualité maîtrisée, mise en œuvre rapide pour exploitation professionnelle.",
    path: "/fabrication",
    keywords:
      "fabrication CLT, bois massif, préfabrication atelier, habitat bois français, construction modulaire bois",
  });

  return (
    <div data-testid="fabrication-page">
      {/* Hero */}
      <section className="pt-20 pb-12">
        <div className="container-yono">
          <p className="eyebrow mb-6">Le matériau · La fabrication</p>
          <h1 className="font-heading text-[28px] sm:text-[34px] lg:text-[44px] tracking-tight leading-[1.08] max-w-4xl">
            Une structure bois massif CLT, fabriquée en atelier en France
          </h1>
          <div className="mt-8 max-w-3xl text-[15px] sm:text-[16px] leading-[1.85] text-[var(--yono-soft-black)]/85 space-y-4">
            <p>
              Toute la gamme YONO est construite à partir de panneaux CLT en
              épicéa de 60 mm. Ce choix constructif associe structure porteuse,
              finition intérieure naturelle et précision d'assemblage.
            </p>
            <p>
              Pensé pour la série, le CLT permet de produire des habitats bois
              robustes, chaleureux et réguliers, avec une qualité maîtrisée
              d'un habitat à l'autre.
            </p>
          </div>
        </div>
      </section>

      {/* Big image */}
      <section className="pb-12">
        <div className="container-yono">
          <div className="aspect-[16/9] border border-[var(--yono-border)] overflow-hidden product-card-img">
            <img
              src="/images/dr9fwbcg-chatgpt-image-24-avr-2026-a-08-37-04.webp"
              alt="CLT structure YONO"
              className="w-full h-full object-cover"
            />
          </div>
          <p className="mt-3 font-mono text-[11px] tracking-[0.12em] uppercase text-[var(--yono-warm-grey)]">
            CLT structure YONO
          </p>
        </div>
      </section>

      <div className="container-yono"><div className="h-line" /></div>

      {/* Le CLT - 8 points */}
      <section className="section">
        <div className="container-yono">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-3">
              <p className="eyebrow">Le matériau</p>
            </div>
            <div className="lg:col-span-9">
              <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] tracking-tight leading-[1.18] max-w-3xl">
                Le CLT, une structure bois robuste et chaleureuse
              </h2>
              <div className="mt-6 text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/85 max-w-3xl space-y-4">
                <p>
                  Le CLT est au cœur du système constructif YONO. Utilisé en
                  structure apparente, il donne aux habitats leur solidité, leur
                  précision et leur ambiance intérieure bois.
                </p>
                <p>
                  Il permet également de rationaliser la fabrication en
                  atelier, avec une logique constructive adaptée aux formats
                  compacts, répétables et professionnels.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {CLT_POINTS.map((b, i) => (
              <div key={b.t} className="border-t border-[var(--yono-border)] pt-5">
                <div className="font-mono text-xs text-[var(--yono-warm-grey)] mb-3">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-heading text-lg mb-2">{b.t}</h3>
                <p className="text-[14px] leading-relaxed text-[var(--yono-warm-grey)]">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="container-yono"><div className="h-line" /></div>

      {/* Atelier */}
      <section className="section bg-[var(--yono-beige)]">
        <div className="container-yono grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <p className="eyebrow mb-6">L'atelier</p>
            <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] tracking-tight leading-[1.18]">
              Des habitats fabriqués en atelier, livrés prêts à installer
            </h2>
            <div className="mt-6 space-y-5 text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/85">
              <p>
                Les habitats YONO sont fabriqués en France, en atelier, avec un
                haut niveau de préfabrication. Chaque habitat est assemblé,
                isolé, équipé et fini avant livraison pour offrir une qualité
                constante et une mise en œuvre rapide.
              </p>
              <p>
                Pour les campings, domaines nature et exploitants HPA, c'est
                un vrai avantage opérationnel : moins de travaux sur place,
                moins d'imprévus, moins de nuisances pour les clients, et des
                hébergements prêts à raccorder dès leur arrivée sur site.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="aspect-[4/3] border border-[var(--yono-border)] overflow-hidden product-card-img">
              <img
                src="/images/neu7pjlj-img-6030.webp"
                alt="Atelier YONO — assemblage des habitats CLT"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 40%" }}
              />
            </div>
            <p className="mt-3 font-mono text-[11px] tracking-[0.12em] uppercase text-[var(--yono-warm-grey)]">
              Atelier YONO préfabrication
            </p>
          </div>
        </div>
      </section>

      <div className="container-yono"><div className="h-line" /></div>

      {/* Mise en oeuvre */}
      <section className="section">
        <div className="container-yono">
          <p className="eyebrow mb-6">Mise en œuvre</p>
          <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[34px] tracking-tight max-w-3xl leading-[1.18]">
            Simple à déployer, conçu pour l'exploitation
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {STEPS.map((s, i) => (
              <div key={s.t} className="border-t border-[var(--yono-border)] pt-5">
                <div className="font-mono text-xs text-[var(--yono-warm-grey)] mb-3">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-heading text-lg mb-2">{s.t}</h3>
                <p className="text-[14px] leading-relaxed text-[var(--yono-warm-grey)]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="container-yono"><div className="h-line" /></div>

      {/* Engagements */}
      <section className="section bg-[var(--yono-beige)]">
        <div className="container-yono">
          <p className="eyebrow mb-6">Engagements</p>
          <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[34px] tracking-tight max-w-3xl leading-[1.18]">
            Un savoir-faire au service des opérateurs
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
            {ENGAGEMENTS.map((e, i) => (
              <div key={e.t} className="border-t border-[var(--yono-border)] pt-5">
                <div className="font-mono text-xs text-[var(--yono-warm-grey)] mb-3">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-heading text-lg mb-2">{e.t}</h3>
                <p className="text-[14px] leading-relaxed text-[var(--yono-warm-grey)]">{e.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container-yono">
          <div className="border border-[var(--yono-border)] p-10 lg:p-16 text-center">
            <h3 className="font-heading text-[26px] lg:text-[34px] tracking-tight">
              Un projet d'hébergement ou un parc à déployer ?
            </h3>
            <p className="mt-4 max-w-2xl mx-auto text-[var(--yono-soft-black)]/75 text-[15px]">
              Échangeons sur votre site, vos contraintes d'exploitation et les
              formats les plus adaptés.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              <Link to="/contact" className="btn-primary">
                Étudier mon projet
              </Link>
              <Link to="/gamme" className="btn-outline">Voir la gamme</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
